import type { DealChain } from './dealChain';
import type { ApiRentalDealItem, ApiRentalContractState } from 'src/types/rental';

import { DealStep } from 'src/types/dealStep';
import { RentalDealMode } from 'src/types/rental';

import { depositLedgerOf } from './rentalClock';
import { formatMoney, formatAmount } from './money';

/**
 * Pure invoice logic for the renter rental flow (spec `rental-invoice-spec.md`). Kept out
 * of the components so it can be unit-tested in the node env (the app has no RTL setup —
 * only vitest specs for pure helpers), and so every screen that shows a rental total
 * builds it from the same lines.
 *
 * Why it exists: the renter used to see three different numbers for one rental — the
 * booking screen and the Bill tab summed rental + deposit while the wallet was asked for
 * rental + deposit + the 9% platform fee that `RentalDeal.sol` charges on top.
 *
 * All amounts here are **base units** (BigInt), like the on-chain mirror, so the fee is
 * the same integer division the contract does — never a float percentage.
 *
 * Two sources, both explicit in the API:
 *  - `estimateRentalInvoice` — before the contract exists (booking screen), from the
 *    listing's own terms.
 *  - `contractRentalInvoice` — once the contract is deployed (pay modal, Bill tab), from
 *    the on-chain mirror. The contract is the authority: its `platformFeeAmount` is taken
 *    as-is rather than recomputed, so the lines always add up to `getTotalRequired()`.
 */

/** `RentalDeal.sol:87` — PLATFORM_FEE_BPS = 900 (9% of the rental, not of the deposit). */
export const PLATFORM_FEE_BPS = 900n;

/** `RentalDeal.sol:88` — BPS_DENOMINATOR = 10000. */
export const BPS_DENOMINATOR = 10000n;

/** The invoice lines of one rental, all in base units. */
export interface RentalInvoiceLines {
  /** Rental for the whole booking (days × daily price). */
  rental: bigint;
  /** Security deposit — refundable when the rental completes. */
  deposit: bigint;
  /** Platform fee, 9% of the rental — non-refundable, taken upfront by `pay()`. */
  fee: bigint;
  /** Extensions already paid on-chain (extra rental + their own fee); `0n` when none. */
  extension: bigint;
}

/** The api counts money in cents: two decimals of the fiat the stablecoin is pegged to. */
const CENT_DECIMALS = 2;

/**
 * Display amount → base units, the api's rule exactly: `convertTokenToBaseUnits` in api
 * `src/libs/helpers/token-conversion.helper.ts:29–42`, which the api deploys the rental from and
 * quotes the booking with. The amount is first taken to whole cents, which drops float noise
 * (`2.01 * 1e6` is 2009999.9999999998, `1.15 * 3` is 3.4499999999999997), then scaled in integers;
 * below two decimals, integer division keeps whole base units, as the api does. Flooring the float
 * instead showed 2.009999 for a rental the contract then asked 2.01 for (app#253).
 */
export const toBaseUnits = (amount: number, decimals: number): bigint => {
  const cents = Math.round(amount * 10 ** CENT_DECIMALS);

  // A listing still missing its economics (draft) sends null/undefined prices; showing a
  // zero line beats crashing the screen on BigInt(NaN).
  if (!Number.isFinite(cents)) return 0n;

  const whole = BigInt(Math.max(0, cents));

  return decimals >= CENT_DECIMALS
    ? whole * 10n ** BigInt(decimals - CENT_DECIMALS)
    : whole / 10n ** BigInt(CENT_DECIMALS - decimals);
};

/**
 * A typed decimal amount → base units, exactly, or `null` when it is not an amount at all.
 *
 * Not `toBaseUnits`, which rounds to whole cents — the api's rule for the amounts a deal stores —
 * and would turn a typed 8.291 into 8.29. Nor a float times a power of ten: `8.29 * 1e6` is
 * 8289999.999999999, and flooring it proposes 8.289999 where the renter typed 8.29. One base
 * unit is nothing to a wallet and everything to a split — two proposals settle a dispute only when
 * they are the *same integer*. So the string is split on its own decimal point and never becomes a
 * float on the way.
 *
 * More decimals than the token has are refused rather than truncated: silently dropping a digit
 * from an amount somebody typed is the same class of wrong.
 */
export const parseTokenAmount = (input: string, decimals: number): bigint | null => {
  const match = /^(\d*)(?:\.(\d*))?$/.exec(input.trim());

  if (!match || (match[1] === '' && !match[2])) {
    return null;
  }

  const fraction = match[2] ?? '';

  if (fraction.length > decimals) {
    return null;
  }

  return BigInt(`${match[1] || '0'}${fraction.padEnd(decimals, '0')}`);
};

/** Base units → display amount (plain number, for formatting only). */
export const fromBaseUnits = (value: bigint, decimals: number): number =>
  Number(value) / 10 ** decimals;

/**
 * Base units → the string shown to the renter. Full token precision on purpose: a line
 * rounded to fewer digits than the chain uses could make the shown lines miss their own
 * total, and the pay modal promises the renter an amount to approve "exactly".
 *
 * Pinned to en-US, not the visitor's locale: the app is English-only, the surrounding
 * amounts render English-style, and a ru/de browser would otherwise print "1 794,3" —
 * a comma decimal separator in a sum the renter is told to approve exactly.
 */
export const formatBaseUnits = (value: bigint, decimals: number): string =>
  formatAmount(fromBaseUnits(value, decimals), decimals);

/**
 * The platform fee of a rental amount, in base units: `amount * 900 / 10000` with integer
 * division — byte-for-byte the math of `RentalDeal.sol:173` (`initialize`) and of the
 * extension charge `approveExtension()` pulls.
 */
export const platformFeeOf = (rentalBaseUnits: bigint): bigint =>
  (rentalBaseUnits * PLATFORM_FEE_BPS) / BPS_DENOMINATOR;

/**
 * Estimate the invoice from a listing, before any contract exists (booking screen).
 *
 * The deposit and the daily price are the dealer's terms in the listing currency; the api
 * converts them to the settlement currency when it deploys the contract, so this is exact
 * for a listing already priced in the settlement currency and proportionally right for any
 * other. The fee is computed with the contract's own integer math, so the share the renter
 * is promised here is the share the wallet will be asked for.
 */
export const estimateRentalInvoice = ({
  rentalAmount,
  depositAmount,
  decimals,
}: {
  /** Rental for the whole booking in display units — days × daily price, as the api stores it. */
  rentalAmount: number;
  depositAmount: number;
  decimals: number;
}): RentalInvoiceLines => {
  const rental = toBaseUnits(rentalAmount, decimals);

  return {
    rental,
    deposit: toBaseUnits(depositAmount, decimals),
    fee: platformFeeOf(rental),
    extension: 0n,
  };
};

/**
 * Exact invoice from the on-chain mirror (pay modal, Bill tab). `platformFeeAmount` is the
 * value the contract itself stored at `initialize()` — taken as-is, never recomputed, so
 * the lines can never promise less than `pay()` will pull (spec §5.2).
 */
export const contractRentalInvoice = (
  state: Pick<
    ApiRentalContractState,
    'rentalAmount' | 'depositAmount' | 'platformFeeAmount' | 'extensionTotal'
  >
): RentalInvoiceLines => ({
  rental: BigInt(state.rentalAmount || '0'),
  deposit: BigInt(state.depositAmount || '0'),
  fee: BigInt(state.platformFeeAmount || '0'),
  extension: BigInt(state.extensionTotal || '0'),
});

/**
 * What the wallet is asked for at `pay()` — mirrors `getTotalRequired()`
 * (`RentalDeal.sol:452`) = deposit + rental + fee. Extensions are charged separately,
 * later, so they are not part of it.
 */
export const rentalTotalRequired = (lines: RentalInvoiceLines): bigint =>
  lines.deposit + lines.rental + lines.fee;

/** Everything this rental has cost so far: the initial payment plus any paid extension. */
export const rentalInvoiceTotal = (lines: RentalInvoiceLines): bigint =>
  rentalTotalRequired(lines) + lines.extension;

/**
 * The stablecoin a rental settles in, and how many decimals it has.
 *
 * From the deal's chain, because that is what actually decides it: a EUR listing on Base settles
 * in EURC, a USD one in USDC, and a legacy TRON rental in USDT. `cryptoCurrency` is api's own
 * label for the payout currency and stands in only while the chain registry has not answered —
 * six decimals is what all three stablecoins have, and it is not a guess about any of them.
 */
export const STABLECOIN_DECIMALS = 6;

export interface RentalToken {
  symbol: string;
  decimals: number;
}

export const rentalToken = (
  rental: Pick<ApiRentalDealItem, 'cryptoCurrency'>,
  chain: DealChain | null
): RentalToken => ({
  symbol: chain?.token?.symbol ?? rental.cryptoCurrency,
  decimals: chain?.token?.decimals ?? STABLECOIN_DECIMALS,
});

interface InvoiceRow {
  label: string;
  value: string;
}

export interface RentalInvoiceView {
  lines: InvoiceRow[];
  total: InvoiceRow;
  /** Money that left the deposit rather than the wallet — below the total, outside the sum. */
  footnote: InvoiceRow | null;
  /** The moment of payment. Null with `issuedFallback` = paid, but this chain records no time. */
  issuedAt: string | null;
  issuedFallback: string;
  paymentTxHash: string | null;
  /** The booking half of the card: how the car is collected, what a late day costs, what is left. */
  meta: InvoiceRow[];
}

/**
 * One rental invoice, in the format both verticals share (contracts v3, UI part §4).
 *
 * What the renter paid, in the lines; what the deposit paid for, as a footnote outside the total.
 * The distinction is the whole reason the footnote exists: overdue days are charged **from the
 * deposit**, so adding them to "total paid" would bill the renter twice for money they had
 * already handed over.
 *
 * The date and the transaction come from the deal's own timeline. A rental on a chain whose
 * poller records no events (the legacy TRON generation) is paid without either — so it says
 * "Paid" rather than inventing a moment or, worse, keeping a paid invoice at "Pending".
 */
export const rentalInvoiceView = (rental: ApiRentalDealItem, chain: DealChain | null): RentalInvoiceView => {
  const state = rental.rentalContractState;
  const token = rentalToken(rental, chain);
  const lines = state
    ? contractRentalInvoice(state)
    : estimateRentalInvoice({
        rentalAmount: rental.rentalAmount,
        depositAmount: rental.depositAmount,
        decimals: token.decimals,
      });
  const ledger = depositLedgerOf(state);
  const paidEvent = rental.stepEvents?.find((event) => event.step === DealStep.RentalPaid) ?? null;
  const toppedUp = (rental.stepEvents ?? [])
    .filter((event) => event.step === DealStep.RentalDepositToppedUp)
    .reduce((sum, event) => sum + safeBaseUnits(event.details?.amount), 0n);

  const money = (value: bigint) => formatMoney(fromBaseUnits(value, token.decimals), token.symbol, token.decimals);
  const rows: InvoiceRow[] = [
    { label: 'Rental', value: money(lines.rental) },
    { label: 'Deposit (refundable)', value: money(lines.deposit) },
    { label: 'Platform fee (9%)', value: money(lines.fee) },
  ];

  if (lines.extension > 0n) {
    rows.push({ label: 'Extension paid (incl. fee)', value: money(lines.extension) });
  }
  if (toppedUp > 0n) {
    rows.push({ label: 'Deposit top-ups', value: money(toppedUp) });
  }

  const meta: InvoiceRow[] = [
    { label: 'Pickup', value: rental.mode === RentalDealMode.DELIVERY ? 'Delivery' : 'Onsite' },
  ];

  if (ledger.chargedAmount > 0n) {
    meta.push({ label: 'Deposit left on the contract', value: money(ledger.remainingDeposit) });
  }

  return {
    lines: rows,
    total: {
      // "Total paid" on a booking nobody paid is a receipt for money that never moved; before the
      // payment the same lines are a quote, and the card already says "Pending" beside them.
      label: state?.isPaid ? 'Total paid' : 'Total',
      value: money(rentalInvoiceTotal(lines) + toppedUp),
    },
    footnote:
      ledger.chargedAmount > 0n
        ? {
            // Not "from your deposit": `topUpDeposit` settles overdue debt straight from the
            // wallet and the contract counts it in the same total, so naming one source would be
            // wrong for exactly the renter who paid up instead of letting the deposit drain.
            label: `Charged for time past the end date${
              ledger.paidDaysTotal > 0 ? ` (${ledger.paidDaysTotal} day${ledger.paidDaysTotal === 1 ? '' : 's'})` : ''
            }`,
            value: money(ledger.chargedAmount),
          }
        : null,
    issuedAt: paidEvent?.occurredAt ?? null,
    issuedFallback: state?.isPaid ? 'Paid' : 'Pending',
    paymentTxHash: paidEvent?.txHash ?? null,
    meta,
  };
};

/** A base-unit amount off an event's details; anything unreadable counts as nothing. */
const safeBaseUnits = (value: unknown): bigint =>
  typeof value === 'string' && /^\d+$/.test(value) ? BigInt(value) : 0n;
